/**
 * Console + localStorage ring-buffer logger.
 *
 * Every entry goes to the console (prefixed) and to an in-memory ring buffer
 * persisted to localStorage (throttled), so entries survive reloads and can be
 * retrieved after an incident on a remote device via `dump()`. On creation the
 * tail of the previous sessions is restored, so a dump keeps cross-session
 * history. Strictly best-effort: storage failures (quota, private mode) never
 * throw — the console output remains.
 */

export interface PersistentLogEntry {
  t: string;
  event: string;
  data?: unknown;
}

export interface PersistentLoggerOptions {
  /** Console prefix, e.g. "[media-diag]". */
  prefix: string;
  /** localStorage key holding the persisted entries. */
  storageKey: string;
  /** While false, log() drops everything (no console, no buffer). Default: true. */
  enabled?: boolean;
  /** Ring buffer size, in memory and in storage. Default: 400. */
  maxEntries?: number;
  /** Throttle for storage writes. Default: 1000ms. */
  persistDelayMs?: number;
}

export interface PersistentLogger {
  log: (event: string, data?: unknown) => void;
  /** Flush, then return every persisted entry (previous sessions included). */
  dump: () => PersistentLogEntry[];
  /** Print every persisted entry to the console as prefixed JSON (copy-friendly). */
  dumpConsole: () => void;
  clear: () => void;
  /** Write pending entries to storage now. */
  flush: () => void;
  setEnabled: (enabled: boolean) => void;
  isEnabled: () => boolean;
}

export const createPersistentLogger = (options: PersistentLoggerOptions): PersistentLogger => {
  const { prefix, storageKey } = options;
  const maxEntries = options.maxEntries ?? 400;
  const persistDelayMs = options.persistDelayMs ?? 1000;
  let enabled = options.enabled ?? true;

  const buffer: PersistentLogEntry[] = [];
  let persistTimeout: ReturnType<typeof setTimeout> | undefined;

  const readPersisted = (): PersistentLogEntry[] => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  };

  const flush = () => {
    if (persistTimeout !== undefined) {
      clearTimeout(persistTimeout);
      persistTimeout = undefined;
    }
    if (!buffer.length) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(buffer.slice(-maxEntries)));
    } catch (_) {}
  };

  // Keep the tail of previous sessions so a post-incident dump still has them.
  const previous = readPersisted();
  if (previous.length) buffer.push(...previous.slice(-Math.floor(maxEntries / 2)));

  const log = (event: string, data?: unknown) => {
    if (!enabled) return;
    const entry: PersistentLogEntry = { t: new Date().toISOString(), event };
    if (data !== undefined) {
      // Clone so the persisted entry cannot be mutated afterwards.
      try {
        entry.data = JSON.parse(JSON.stringify(data));
      } catch (_) {
        entry.data = String(data);
      }
    }
    buffer.push(entry);
    if (buffer.length > maxEntries) buffer.splice(0, buffer.length - maxEntries);
    if (data !== undefined) console.log(prefix, event, data);
    else console.log(prefix, event);
    if (persistTimeout === undefined) persistTimeout = setTimeout(flush, persistDelayMs);
  };

  const dump = (): PersistentLogEntry[] => {
    flush();
    return readPersisted();
  };

  const dumpConsole = () => {
    console.log(prefix, JSON.stringify(dump(), null, 1));
  };

  const clear = () => {
    buffer.length = 0;
    if (persistTimeout !== undefined) {
      clearTimeout(persistTimeout);
      persistTimeout = undefined;
    }
    try {
      localStorage.removeItem(storageKey);
    } catch (_) {}
  };

  const setEnabled = (value: boolean) => {
    if (!value) flush();
    enabled = value;
  };

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flush();
    });
  }

  return { log, dump, dumpConsole, clear, flush, setEnabled, isEnabled: () => enabled };
};
